import React, { useState } from 'react';

/**
 * Unified navigation (DESIGN-SYSTEM.md §3, extended #62 for the CRM suite per UXeng's reconciliation —
 * fleet-reports/UXeng-stitch-crm-reconciliation.md §a.4/§b.0a): THREE tiers off one item list —
 *   desktop  (>=1024, `lg:`)  full rail, labels + optional group headers
 *   tablet   (768-1023, `md:`) icon-only rail, `nav-rail-icon` (72px) wide, label on hover/focus
 *   mobile   (<768)           fixed bottom bar, primary items + "More" bottom sheet
 * This is a structural RESKIN of the console's existing tabs — each item carries the caller's original
 * `testid` (e.g. `tab-visitors`) and `onSelect(id)` wires to the same `setActiveTab`, so the functional
 * cypress specs that click those testids keep passing. Look changes; the control does not.
 */
export interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  testid: string;
  group?: string; // desktop/tablet group header (e.g. "ENGAGE"); omit for ungrouped items
}

interface AppNavProps {
  items: NavItem[];
  activeId: string;
  onSelect: (id: string) => void;
  brand?: React.ReactNode;   // workspace/logo slot (top of rail) — see Brand below
  footer?: React.ReactNode;  // profile/logout slot (bottom of rail)
}

const railItemCls = (active: boolean) =>
  `group relative w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
    active ? 'bg-primary text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'
  }`;

const barItemCls = (active: boolean) =>
  `flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${
    active ? 'text-white' : 'text-slate-400'
  }`;

/**
 * Tokenized brand lockup (GTM ruling, #62): bare wordmark, no descriptor line, no tenant name — the
 * brand answers "who makes this", not "whose data am I looking at". One lockup, not four class
 * combinations. Desktop (>=1024) shows the wordmark; the tablet icon-rail (768-1023) shows the
 * monogram so the rail doesn't sit flush to the top edge; mobile renders nothing (BottomBar has no
 * brand slot — a phone doesn't need telling which app it's in).
 */
export const Brand: React.FC = () => (
  <div className="flex items-center justify-center lg:justify-start text-white tracking-tight">
    <span className="hidden lg:inline text-headline-md">Biombo</span>
    <span className="lg:hidden text-headline-md" aria-label="Biombo">B</span>
  </div>
);

/** Group header, desktop-only (the icon-only tablet tier has no room for text labels). */
const GroupHeader: React.FC<{ label: string }> = ({ label }) => (
  <div className="hidden lg:block px-3 pt-4 pb-1 text-label-caps text-slate-500">{label}</div>
);

/** Desktop rail (full, >=1024) + tablet icon-rail (72px, 768-1023). One component, one width tier. */
export const NavRail: React.FC<AppNavProps> = ({ items, activeId, onSelect, brand, footer }) => {
  let lastGroup: string | undefined;
  return (
    <aside className="hidden md:flex md:w-nav-rail-icon lg:w-64 flex-shrink-0 flex-col justify-between bg-slate-nav border-r border-slate-800 z-20">
      <div className="flex flex-col min-h-0">
        {brand && <div className="px-4 py-4 border-b border-slate-800">{brand}</div>}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map((it) => {
            const showHeader = it.group && it.group !== lastGroup;
            lastGroup = it.group;
            return (
              <React.Fragment key={it.id}>
                {showHeader && <GroupHeader label={it.group as string} />}
                <button
                  data-testid={it.testid}
                  onClick={() => onSelect(it.id)}
                  aria-current={activeId === it.id ? 'page' : undefined}
                  className={railItemCls(activeId === it.id)}
                >
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">{it.icon}</span>
                  <span className="hidden lg:inline">{it.label}</span>
                  {/* Tablet icon-only tier: label surfaces as a hover/focus tooltip (UXeng §b.0a). */}
                  <span
                    className="lg:hidden pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap rounded-md bg-slate-nav border border-slate-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity z-30"
                  >
                    {it.label}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </nav>
      </div>
      {footer && <div className="hidden lg:block px-3 py-3 border-t border-slate-800">{footer}</div>}
    </aside>
  );
};

interface BottomBarProps {
  items: NavItem[];        // primary destinations (max ~4) — always visible
  overflow?: NavItem[];    // remaining destinations — behind "More"
  activeId: string;
  onSelect: (id: string) => void;
  moreLabel?: string;      // i18n — caller supplies (this primitive has no translation access)
  moreSheetTitle?: string; // i18n — sheet header, defaults to moreLabel
}

/**
 * Mobile bottom bar (<768). Primary items + testids match the rail's, unchanged. "More" opens the
 * ratified bottom sheet (UXeng §b.0/§b.0a) for whatever doesn't fit the 4 primary slots — no item
 * renders anywhere unless it routes to a real screen (no placeholders, no dead links).
 */
export const BottomBar: React.FC<BottomBarProps> = ({ items, overflow = [], activeId, onSelect, moreLabel = 'More', moreSheetTitle }) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const overflowActive = overflow.some((it) => it.id === activeId);

  return (
    <>
      <nav className="flex md:hidden fixed bottom-0 left-0 right-0 z-50 justify-around gap-1 px-2 py-1.5 bg-slate-nav border-t border-slate-800 pb-[env(safe-area-inset-bottom)]">
        {items.map((it) => (
          <button
            key={it.id}
            data-testid={`${it.testid}-mobile`}
            onClick={() => onSelect(it.id)}
            aria-current={activeId === it.id ? 'page' : undefined}
            className={barItemCls(activeId === it.id)}
          >
            <span className="w-5 h-5 flex items-center justify-center">{it.icon}</span>
            <span>{it.label}</span>
          </button>
        ))}
        {overflow.length > 0 && (
          <button
            data-testid="tab-more-mobile"
            onClick={() => setMoreOpen(true)}
            aria-current={overflowActive ? 'page' : undefined}
            className={barItemCls(overflowActive)}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" />
              </svg>
            </span>
            <span>{moreLabel}</span>
          </button>
        )}
      </nav>
      {moreOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/30 z-[60]" onClick={() => setMoreOpen(false)} />
          <div
            data-testid="more-sheet"
            className="md:hidden fixed inset-x-0 bottom-0 z-[70] bg-surface rounded-t-2xl border-t border-border-neutral shadow-xl pb-[env(safe-area-inset-bottom)]"
          >
            <div className="px-3 pt-3 pb-1 text-label-caps text-text-muted">{moreSheetTitle ?? moreLabel}</div>
            <div className="p-1 pb-3">
              {overflow.map((it) => (
                <button
                  key={it.id}
                  data-testid={`${it.testid}-mobile`}
                  onClick={() => { onSelect(it.id); setMoreOpen(false); }}
                  aria-current={activeId === it.id ? 'page' : undefined}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-body-md ${
                    activeId === it.id ? 'bg-canvas text-primary font-semibold' : 'text-text-strong'
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center">{it.icon}</span>
                  <span>{it.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
